import React from 'react';
import styles from '../../../styles/DocumentManagement.module.css';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';

interface UserRemindersProps {
  variant?: 'page' | 'inline';
  onReminderClick?: (reminder: any) => void;
}

const UserReminders: React.FC<UserRemindersProps> = ({ variant = 'page', onReminderClick }) => {
  const { userReminders, agencyReminders, documentTypes, documentFields } = useDocumentContext();
  const { hasPermission } = useEnhancedAuth();
  const isManagerOrAdmin = hasPermission('user_manage_agency');

  const remindersData = isManagerOrAdmin
    ? (agencyReminders ?? { documentReminders: [], fieldReminders: [] })
    : { documentReminders: (userReminders as any)?.documentReminders ?? [], fieldReminders: (userReminders as any)?.fieldReminders ?? [] };

  const documentReminders: any[] = Array.isArray(remindersData.documentReminders) ? remindersData.documentReminders : [];
  const fieldReminders: any[] = Array.isArray(remindersData.fieldReminders) ? remindersData.fieldReminders : [];
  const total = documentReminders.length + fieldReminders.length;

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleDateString(); } catch { return ''; }
  };

  const recipientName = (reminder: any): string | null => {
    if (!isManagerOrAdmin) return null;
    const p = reminder.profiles;
    if (!p) return null;
    return [p.full_names, p.last_name].filter(Boolean).join(' ') || null;
  };

  const handleClick = (reminder: any) => {
    if (!onReminderClick) return;
    // Resolve the full document type or field from the structural map so
    // navAndHighlightNavigate has subcategory_id / category_id to expand.
    const resolvedType = reminder.document_type_id
      ? (documentTypes.find(t => t.id === reminder.document_type_id) ?? null)
      : null;
    const resolvedField = reminder.field_type_id
      ? (documentFields.find(f => f.id === reminder.field_type_id) ?? null)
      : null;
    onReminderClick({ ...reminder, _resolvedType: resolvedType, _resolvedField: resolvedField });
  };

  if (variant === 'inline') {
    return (
      <div className={styles.documentsOverviewRemindersList}>
        {total === 0 ? (
          <div className={styles.documentsOverviewEmpty}>No reminders found.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...documentReminders.map((r: any) => ({ ...r, _kind: 'doc' })), ...fieldReminders.map((r: any) => ({ ...r, _kind: 'field' }))].map((reminder: any) => {
              const name = recipientName(reminder);
              const label = reminder._kind === 'doc'
                ? `📄 ${reminder.filing_document_types?.name || 'Document reminder'}`
                : `✏️ ${reminder.filing_field_types?.name || 'Field reminder'}`;
              return (
                <button
                  key={reminder.id}
                  type="button"
                  className={styles.documentsOverviewReminderCard}
                  onClick={() => handleClick(reminder)}
                  style={{ textAlign: 'left', width: '100%', cursor: onReminderClick ? 'pointer' : 'default' }}
                >
                  {name && <span className={styles.documentsOverviewReminderUser}>For: {name}</span>}
                  <span className={styles.documentsOverviewReminderText}>{label}</span>
                  {reminder.created_at && (
                    <span className={styles.documentsOverviewReminderDueDate}>Sent: {formatDate(reminder.created_at)}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.documentManagementHeader}>
        <h1 className={styles.documentManagementTitle}>Reminders</h1>
        <p className={styles.documentManagementSubtitle}>
          {isManagerOrAdmin ? "Reminders sent to users across your agency." : "Reminders sent to you for outstanding documents and fields."}
        </p>
      </div>

      <div className={styles.documentManagementSection}>
      <h3>Reminders {total > 0 && <span style={{ fontWeight: 400, fontSize: '1rem', color: '#6b7280' }}>({total})</span>}</h3>

      {total === 0 ? (
        <div className={styles.documentsOverviewEmpty}>No pending reminders.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {documentReminders.length > 0 && (
            <section>
              <p style={{ fontWeight: 600, color: '#374151', marginBottom: 10 }}>📄 Document Reminders</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {documentReminders.map((reminder: any) => {
                  const name = recipientName(reminder);
                  return (
                    <button
                      key={reminder.id}
                      type="button"
                      className={styles.documentsOverviewReminderCard}
                      onClick={() => handleClick(reminder)}
                      style={{ textAlign: 'left', width: '100%', cursor: onReminderClick ? 'pointer' : 'default' }}
                    >
                      {name && (
                        <span className={styles.documentsOverviewReminderUser}>For: {name}</span>
                      )}
                      <span className={styles.documentsOverviewReminderText}>
                        {reminder.filing_document_types?.name || 'Document reminder'}
                      </span>
                      {reminder.created_at && (
                        <span className={styles.documentsOverviewReminderDueDate}>Sent: {formatDate(reminder.created_at)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {fieldReminders.length > 0 && (
            <section>
              <p style={{ fontWeight: 600, color: '#374151', marginBottom: 10 }}>✏️ Field Reminders</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {fieldReminders.map((reminder: any) => {
                  const name = recipientName(reminder);
                  return (
                    <button
                      key={reminder.id}
                      type="button"
                      className={styles.documentsOverviewReminderCard}
                      onClick={() => handleClick(reminder)}
                      style={{ textAlign: 'left', width: '100%', cursor: onReminderClick ? 'pointer' : 'default' }}
                    >
                      {name && (
                        <span className={styles.documentsOverviewReminderUser}>For: {name}</span>
                      )}
                      <span className={styles.documentsOverviewReminderText}>
                        {reminder.filing_field_types?.name || 'Field reminder'}
                      </span>
                      {reminder.created_at && (
                        <span className={styles.documentsOverviewReminderDueDate}>Sent: {formatDate(reminder.created_at)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default UserReminders;
